/**
 * Maps Stage-1 structured findings into contextual key findings for the
 * Feasibility Profile section, with intent-aware categories and nuclear gates.
 */
import type {
  ContextualFindingCategory,
  ContextualKeyFinding,
  ProjectIntent,
} from '../types/siteRag';
import type { StructuredAnalysis } from '../types/siteEvidence';
import { summarizeProjectIntent } from './projectIntentSummary';
import { applyNuclearRelevanceGate } from './nuclearFindingRelevance';

/** Default validation line on contextual key findings (Feasibility Profile). */
export const DEFAULT_CONTEXTUAL_VALIDATION_WARNING =
  'Verify against your site documents before acting on this.';

type RawFinding = {
  title: string;
  detail: string;
  sedSection?: string;
  impact?: 'positive' | 'negative' | 'neutral' | string;
  rank?: number;
};

function normalizeImpact(impact: string | undefined): 'positive' | 'negative' | 'neutral' {
  if (impact === 'positive' || impact === 'negative') return impact;
  return 'neutral';
}

function categorizeFinding(title: string, detail: string, intentSummary: string | null): ContextualFindingCategory {
  const blob = `${title} ${detail}`.toLowerCase();
  if (/intent|timeline|capacity|phased|nuclear interest|reactor design|cooling strategy|workload|haleu|epz/.test(blob)) {
    return 'intentAlignment';
  }
  if (/grid|interconnection|seismic|flood|zoning|parcel|water|habitat|utility|pipeline|site area/.test(blob)) {
    return 'projectDetails';
  }
  return 'outcomeInfluence';
}

function defaultUpdateField(category: ContextualFindingCategory): { path: string; label: string } {
  switch (category) {
    case 'intentAlignment':
      return { path: 'projectIntent', label: 'Project Intent' };
    case 'projectDetails':
      return { path: 'siteContext', label: 'Site Context' };
    default:
      return { path: 'projectIntent.priority', label: 'Project priority / decision criteria' };
  }
}

function mapRawToContextual(
  f: RawFinding,
  intent: ProjectIntent | undefined,
  intentSummary: string | null,
): ContextualKeyFinding {
  const category = categorizeFinding(f.title, f.detail, intentSummary);
  const { path, label } = defaultUpdateField(category);
  const impact = normalizeImpact(f.impact);

  let base: ContextualKeyFinding = {
    title: f.title,
    category,
    detail: f.detail,
    // CTA-SCRUB-2 (2026-07-03) — NEVER persist app-onboarding copy into a
    // report. The old fallback ('Set Project Intent to see…') is UI chrome;
    // it leaked into generated reports whenever intent was absent at gen
    // (live o2ogkv ×5 via the agent path's unthreaded intent). Empty string
    // ⇒ the renderer omits the Intent-Context row entirely.
    intentContext: intentSummary ? `Evaluated in context of: ${intentSummary}.` : '',
    influences: category === 'outcomeInfluence'
      ? 'Feasibility scores, BTM technology ranking, and development timeline assumptions.'
      : category === 'projectDetails'
        ? 'Site layout, permitting pathway, and infrastructure sizing.'
        : 'Report framing, technology prioritization, and go/no-go recommendation.',
    whyKey: f.rank != null && f.rank <= 3
      ? `Ranked #${f.rank} by materiality to site feasibility and decision impact.`
      : 'Surfaced as a material constraint or enabler from site evidence analysis.',
    validationWarning: DEFAULT_CONTEXTUAL_VALIDATION_WARNING,
    updateFieldPath: path,
    updateFieldLabel: label,
    impact,
    source: f.sedSection,
  };

  base = applyNuclearRelevanceGate(base, intent) as ContextualKeyFinding;
  return base;
}

/** Legacy structuredFindings shape → contextual findings (back-compat). */
export function legacyFindingsToContextual(
  findings: { title: string; detail: string; source: string; impact: string }[],
  intent: ProjectIntent | undefined,
): ContextualKeyFinding[] {
  const intentSummary = summarizeProjectIntent(intent);
  return findings.slice(0, 5).map((f, i) =>
    mapRawToContextual(
      { title: f.title, detail: f.detail || f.title, sedSection: f.source, impact: f.impact, rank: i + 1 },
      intent,
      intentSummary,
    ),
  );
}

export function enrichContextualFindings(
  structuredAnalysis: StructuredAnalysis | null | undefined,
  projectIntent: ProjectIntent | undefined,
  legacyStructuredFindings?: { title: string; detail: string; source: string; impact: string }[],
): ContextualKeyFinding[] {
  const intentSummary = summarizeProjectIntent(projectIntent);
  const raw: RawFinding[] = structuredAnalysis?.findings?.length
    ? structuredAnalysis.findings.slice(0, 5).map(f => ({
        title: f.title,
        detail: f.detail,
        sedSection: f.sedSection,
        impact: f.impact,
        rank: f.rank,
      }))
    : (legacyStructuredFindings ?? []).map((f, i) => ({
        title: f.title,
        detail: f.detail || f.title,
        sedSection: f.source,
        impact: f.impact,
        rank: i + 1,
      }));

  if (raw.length === 0) return [];
  return raw.map(f => mapRawToContextual(f, projectIntent, intentSummary));
}
